
// src/features/dashboard/components/EmployeeCompliance/ComplianceHeatmap.tsx

import { useState } from "react";

import type {
  ComplianceHeatmapRow,
} from "../../types/Employee/employeeCompliance.types";

interface Props {
  data: ComplianceHeatmapRow[];
  period: string;
  subtitle: string;
}

const ComplianceHeatmap = ({
  data,
  period,
  subtitle,
}: Props) => {
  const [hovered, setHovered] = useState<{
    gate: string;
    day: number;
    status: string;
  } | null>(null);

  const [activeLegend, setActiveLegend] =
    useState<"Met" | "Missed" | null>(null);

  const dayCount = data[0]?.days.length ?? 0;

  return (
    <div
      className="
        relative
        overflow-visible
        rounded-xl
        border
        border-[#D9DEE8]
        bg-white
        px-5
        pb-4
        pt-12
      "
    >
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-[16px] font-semibold text-[#002B5B]">
            Monthly Compliance Scorecard
          </h3>

          <div
            className="
              rounded-full
              bg-[#EAF2FF]
              px-3
              py-[4px]
              text-[11px]
              font-medium
              text-[#2563EB]
            "
          >
            {period} · {subtitle}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-5 flex justify-center gap-5">
        {(["Missed", "Met"] as const).map((status) => {
          const isActive =
            activeLegend === null || activeLegend === status;

          return (
            <div
              key={status}
              className="flex cursor-pointer items-center gap-2"
              onMouseEnter={() => setActiveLegend(status)}
              onMouseLeave={() => setActiveLegend(null)}
            >
              <span
                className="h-3 w-3 rounded-[3px]"
                style={{
                  backgroundColor:
                    status === "Met" ? "#CFF7DB" : "#FDE8E8",
                  opacity: isActive ? 1 : 0.25,
                }}
              />

              <span
                className="text-[12px]"
                style={{
                  opacity: isActive ? 1 : 0.4,
                }}
              >
                {status}
              </span>
            </div>
          );
        })}
      </div>

      {/* Heatmap */}
      <div className="overflow-x-auto pt-10">
        <div
          className="grid items-center gap-y-2"
          style={{
            gridTemplateColumns: `70px repeat(${dayCount}, 46px)`,
          }}
        >
          {data.map((row) => (
            <div
              key={row.gate}
              className="contents"
            >
              {/* Row label */}
              <div
                className="
                  pr-3
                  text-right
                  text-[13px]
                  font-medium
                  text-[#334155]
                "
              >
                {row.gate}
              </div>

              {/* Daily cells */}
              {row.days.map((cell) => {
                const isHovered =
                  hovered?.gate === row.gate &&
                  hovered.day === cell.day;

                const isHighlighted =
                  activeLegend === null ||
                  activeLegend === cell.status;

                return (
                  <div
                    key={`${row.gate}-${cell.day}`}
                    className="relative flex justify-center"
                    onMouseEnter={() =>
                      setHovered({
                        gate: row.gate,
                        day: cell.day,
                        status: cell.status,
                      })
                    }
                    onMouseLeave={() => setHovered(null)}
                  >
                    <div
                      className="
                        h-6
                        w-11
                        rounded-[4px]
                        border
                        border-white
                        transition-all
                      "
                      style={{
                        backgroundColor:
                          cell.status === "Met"
                            ? "#CFF7DB"
                            : "#FDE8E8",
                        opacity: isHighlighted ? 1 : 0.25,
                      }}
                    />

                    {isHovered && (
                      <div
                        className={`
                          absolute
                          z-20
                          whitespace-nowrap
                          rounded-md
                          bg-[#344054]
                          px-3
                          py-2
                          text-[11px]
                          text-white
                          shadow-lg
                          ${
                            row.gate === "Login"
                              ? "top-8"
                              : "-top-12"
                          }
                        `}
                      >
                        <div>{row.gate}</div>
                        <div>Day {cell.day}</div>
                        <div>{cell.status}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Bottom day labels */}
        <div
          className="mt-3 grid"
          style={{
            gridTemplateColumns: `70px repeat(${dayCount}, 46px)`,
          }}
        >
          <div />

          {data[0]?.days.map((cell) => (
            <div
              key={cell.day}
              className="text-center text-[12px] text-[#475467]"
            >
              D{cell.day}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComplianceHeatmap;
